import {useEffect, useMemo, useState} from 'react';
import {collection, doc, onSnapshot, query, where} from 'firebase/firestore';
import {db} from '../../Security/firebase-config';
import {eventSlots} from '../../utils/eventSetup';
import {isReviewableVideo} from '../../utils/overviewVodValidity';
import {activeReviewItem, hasPendingResourceNotes, pendingResourceNoteIds, resourceReviewState} from '../../utils/resourceReview';
import {isCompleteTeamStats} from './teamStatsCompletion';

export const isAddedOverviewVod = data => activeReviewItem(data) && !data?.reviewMediaDeleted && !['deleted','deleting','failed','error','recording','metadata_only'].includes(String(data?.status || data?.processingStatus || '').toLowerCase()) && (isReviewableVideo(data) || ['uploading','processing','queued'].includes(String(data?.status || data?.processingStatus || '').toLowerCase()));
const rows = snapshot => snapshot.docs.map(item => ({...item.data(), id: item.id}));

// Watch canonical match notes and older per-video notes, including legacy replies.
function watchNotes(root, person, changed) {
  let alive = true, notes, replies, failed = false;
  const nested = new Map(), stops = [];
  const emit = () => {
    if (!alive) return;
    const ready = notes && replies && [...nested.values()].every(item => item.rows);
    const allReplies=[
      ...(replies || []), ...[...nested.values()].flatMap(item => item.rows || []),
    ];
    changed({pending:!ready||failed||hasPendingResourceNotes(notes||[],allReplies,person.uid,person.username),checking:!ready,error:failed,noteIds:ready&&!failed?pendingResourceNoteIds(notes||[],allReplies,person.uid,person.username):[]});
  };
  const error = () => {failed = true; emit();};
  stops.push(onSnapshot(collection(db, ...root, 'notes'), snapshot => {
    if (!alive) return;
    notes = rows(snapshot);
    if (root.includes('videos')) {
      const ids = new Set(notes.filter(activeReviewItem).map(item => item.id));
      for (const [id, item] of nested) if (!ids.has(id)) {item.stop(); nested.delete(id);}
      ids.forEach(id => {if (!nested.has(id)) {
        const item = {}; nested.set(id, item);
        item.stop = onSnapshot(collection(db, ...root, 'notes', id, 'replies'), replySnapshot => {
          if (!alive || nested.get(id) !== item) return;
          item.rows = rows(replySnapshot).map(reply => ({...reply, noteId: id})); emit();
        }, error);
      }});
    }
    emit();
  }, error));
  stops.push(onSnapshot(collection(db, ...root, 'allReplies'), snapshot => {replies = rows(snapshot); emit();}, error));
  return () => {alive = false; stops.forEach(stop => stop()); nested.forEach(item => item.stop());};
}

export default function useEventResourcePresence(event, desktop, uid, username) {
  const signature = JSON.stringify(eventSlots(event).map(slot => [slot.mapId, slot.matchId, slot.statsId, desktop ? slot.localMatchId : null]));
  const teamId = event.__teamId, identity = JSON.stringify([teamId, signature, uid, username]);
  const targets = useMemo(() => {
    const items = new Map();
    for (const [mapId, matchId, statsId, localMatchId] of JSON.parse(signature)) {
      if (mapId && matchId) items.set('vod:' + mapId + ':' + matchId, {kind:'vod', mapId, matchId});
      if (statsId) items.set('stats:' + statsId, {kind:'stats', mapId, resourceId:statsId});
      if (localMatchId) items.set('local:' + mapId + ':' + localMatchId, {kind:'local-vod', mapId:mapId || '', resourceId:localMatchId});
    }
    return [...items].map(([key, item]) => ({key, ...item}));
  }, [signature]);
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!teamId) return undefined;
    let alive = true;
    const stops = [], person = {uid, username};
    const update = (key, value) => {if (alive) setState(current => ({identity, values:{...(current?.identity === identity ? current.values : {}), [key]:value}}));};
    targets.forEach(target => {
      const {key, kind, mapId, resourceId} = target;
      if (kind === 'stats') {
        let data, error = false;
        const emit = () => update(key, {items:data && activeReviewItem(data) ? [{viewed:isCompleteTeamStats(data)}] : [], scores:data&&activeReviewItem(data)?[{mapId,score:data.score}]:[], error});
        stops.push(onSnapshot(doc(db, 'teams', teamId, 'matches', resourceId), snapshot => {data = snapshot.exists() ? snapshot.data?.() || {} : null; error = false; emit();}, () => {error = true; emit();}));
      } else if (kind === 'local-vod') {
        let items = [], loading = true, error = false;
        const emit = () => update(key, {items:items.map(item => ({...item, viewed:true})), checking:loading, error});
        let generation = 0;
        const load = async () => {
          const token = ++generation;
          try {
            const records = await window.desktopAPI?.listLocalRecordings?.();
            const matches = (records || []).filter(record => {
              const meta = record.metadata || {}, draft = meta.uploadDraft || {};
              const id = draft.resolvedMatchId || (draft.matchMode === 'existing' ? draft.selectedMatchId : '') || meta.matchId;
              return meta.teamId === teamId && meta.mapId === mapId && id === resourceId;
            });
            const loaded = await Promise.all(matches.map(async record => {
              const review = await window.desktopAPI.readLocalRecordingNotes(record.path);
              return {pending:hasPendingResourceNotes(review?.notes || [], review?.replies || [], uid, username),noteIds:pendingResourceNoteIds(review?.notes||[],review?.replies||[],uid,username).map(id=>JSON.stringify([mapId,id]))};
            }));
            if (!alive || token !== generation) return;
            items = loaded; error = false;
          } catch (_) {if (!alive || token !== generation) return; error = true;}
          loading = false; emit();
        };
        void load(); window.addEventListener('focus', load);
        stops.push(() => {generation++; window.removeEventListener('focus', load);});
      } else {
        const videos = new Map(); let matchPending = true, matchNotes=[], matchChecking=true, matchError=false, received = false, error = false;
        const emit = () => update(key, {items:[...videos.values()].map(item => ({viewed:true, pending:matchPending || item.pending, processing:item.processing})), noteIds:videos.size?[...matchNotes,...[...videos.values()].flatMap(item=>item.noteIds||[])].map(id=>JSON.stringify([mapId,id])):[], checking:!received||(!!videos.size&&(matchChecking||[...videos.values()].some(item=>item.checking))), error:error||(!!videos.size&&(matchError||[...videos.values()].some(item=>item.error)))});
        stops.push(watchNotes(['teams', teamId, 'maps', mapId, 'matches', target.matchId], person, value => {matchPending = value.pending; matchNotes=value.noteIds; matchChecking=value.checking; matchError=value.error; if (received) emit();}));
        stops.push(onSnapshot(query(collection(db, 'teams', teamId, 'maps', mapId, 'videos'), where('matchId', '==', target.matchId)), snapshot => {
          if (!alive) return;
          const added = rows(snapshot).filter(isAddedOverviewVod), ids = new Set(added.map(item => item.id));
          for (const [id, item] of videos) if (!ids.has(id)) {item.stop(); videos.delete(id);}
          added.forEach(data => {
            let item = videos.get(data.id);
            if (!item) {
              item = {pending:true, checking:true, stop:() => {}}; videos.set(data.id, item);
              const current = item, changed = () => {if (alive && videos.get(data.id) === current) emit();};
              const stopNotes = watchNotes(['teams', teamId, 'maps', mapId, 'videos', data.id], person, value => {current.pending = value.pending; current.noteIds=value.noteIds; current.checking=value.checking; current.error=value.error; changed();});
              item.stop = stopNotes;
            }
            item.processing = ['uploading','processing','queued'].includes(String(data.status || data.processingStatus || '').toLowerCase());
          });
          received = true; error = false; emit();
        }, () => {error = true; emit();}));
        stops.push(() => {videos.forEach(item => item.stop()); videos.clear();});
      }
    });
    return () => {alive = false; stops.forEach(stop => stop());};
  }, [identity, teamId, targets, uid, username]);
  const values = state?.identity === identity ? state.values : {};
  const result = (kind,slot) => {
    const selected = targets.filter(target => (target.kind === kind || (kind === 'vod' && target.kind === 'local-vod'))&&(!slot||(target.kind==='stats'?target.resourceId===slot.statsId:target.kind==='local-vod'?target.resourceId===slot.localMatchId&&target.mapId===(slot.mapId||''):target.matchId===slot.matchId&&target.mapId===slot.mapId)));
    const review=resourceReviewState(selected.flatMap(target => values[target.key]?.items || []),
      selected.some(target => !values[target.key] || values[target.key].checking), selected.some(target => values[target.key]?.error));
    return {...review,unreadCount:new Set(selected.flatMap(target=>values[target.key]?.noteIds||values[target.key]?.items?.flatMap(item=>item.noteIds||[])||[])).size,scores:selected.flatMap(target=>values[target.key]?.scores||[])};
  };
  return {vod:result('vod'), stats:result('stats'),bySlot:Object.fromEntries(eventSlots(event).map(slot=>[slot.id,{vod:result('vod',slot),stats:result('stats',slot)}]))};
}

